import moment from 'moment';
import styled from '@emotion/styled';
import { useCallback, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Download24Icon } from '@kep-ui-kit/ui-icons';
import { TableContainer } from '@kep-ui-kit/ui-components';
import { withErrorHandling } from '@selfserve/react-hocs';
import {
  ApplicationFeatures,
  buttonIds,
  invoiceColumnNames,
  PageType,
  trackPageLoadEvent
} from '@selfserve/react-utils';
import {
  StyledTable,
  TableHeadContent,
  TableBodyContent,
} from '../ui/invoice-history-table';
import { IContextData } from './account-layout';

export const StyledButton = styled('button')(({ theme }: { theme?: any }) => ({
  background: 'transparent',
  border: 'none',
  color: '#006EFA',
  outlineColor: '#006EFA',
  cursor: 'pointer',
  svg: {
    pointerEvents: 'none',
  },
}));

export const getDownLoadTag = (downloadUrl: string) => {
  const tag = document.createElement('a');
  tag.href = downloadUrl;
  tag.className = 'export-button';
  tag.rel = 'noreferrer';
  tag.target = '_blank';
  tag.setAttribute('data-id', buttonIds.downloadInvoice);
  document.body.appendChild(tag);
  return tag;
};

const numberWithCommas = (amount: string) =>
  amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const InvoiceHistory = withErrorHandling(({ openErrorModal }) => {
  const { subscriptionOverviewData } = useOutletContext<IContextData>();

  const downloadFile = useCallback(
    (tag: HTMLAnchorElement) => () => {
      tag.click();
      document.body.removeChild(tag);
    },
    []
  );

  const createRows = useCallback(
    (subscriptionOverviewData: object) => {
      const { invoices }: { invoices?: [] } = subscriptionOverviewData;
      return (
        invoices
          ?.map((data) => {
            const { created_at, id, amount, status, downloadUrl } = data;
            const invoiceDate =
              created_at && moment.unix(created_at).format('MMM DD, YYYY');
            const formatedAmount = numberWithCommas(amount);

            return {
              [invoiceColumnNames.ISSUED_DATE]: invoiceDate,
              [invoiceColumnNames.INVOICE_NUMBER]: id,
              [invoiceColumnNames.AMOUNT]: `$${formatedAmount}`,
              [invoiceColumnNames.STATUS]: status,
              [invoiceColumnNames.ICON]: (
                <StyledButton
                  onClick={downloadFile(getDownLoadTag(downloadUrl))}
                  id={buttonIds.downloadInvoice}
                >
                  <Download24Icon />
                </StyledButton>
              ),
            };
          })
          .sort(
            (a, b) =>
              moment(b[invoiceColumnNames.ISSUED_DATE] || '').valueOf() -
              moment(a[invoiceColumnNames.ISSUED_DATE] || '').valueOf()
          ) || []
      );
    },
    [downloadFile]
  );

  useEffect(() => {
    trackPageLoadEvent(
      PageType.View,
      'account_management_invoice_history_page',
      ApplicationFeatures.ACCOUNT_MANAGEMENT,
    );
  }, []);

  return (
    <TableContainer sx={{ maxHeight: '100vh' }}>
      <StyledTable stickyHeader aria-label="sticky table p-element ">
        <TableHeadContent />
        <TableBodyContent rows={createRows(subscriptionOverviewData || {})} />
      </StyledTable>
    </TableContainer>
  );
});
